const API_BASE_URL = "https://translate.duxly.eu";
const TOKEN_EXPIRY_BUFFER_SECONDS = 10;
const TOKEN_RETRY_DELAYS_MS = [0, 250, 750, 1_500, 3_000, 5_000];

export class ApiError extends Error {
	status: number;
	reason?: string;
	installUrl?: string;

	constructor(message: string, status: number, body?: Record<string, unknown>) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		if (body && typeof body.reason === "string") this.reason = body.reason;
		if (body && typeof body.install_url === "string")
			this.installUrl = body.install_url;
	}
}

/**
 * Returns the shop domain from the URL query params (?shop=...).
 */
export function getShopDomain(): string | null {
	return (
		new URLSearchParams(window.location.search).get("shop") ??
		window.__shopifyShop
	);
}

function wait(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
	const payload = token.split(".")[1];
	if (!payload) return null;

	try {
		const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
		const padded = base64.padEnd(
			base64.length + ((4 - (base64.length % 4)) % 4),
			"=",
		);
		return JSON.parse(window.atob(padded)) as Record<string, unknown>;
	} catch {
		return null;
	}
}

function isUsableJwt(token: string): boolean {
	const payload = decodeJwtPayload(token);
	if (!payload) return true;

	const exp = payload.exp;
	if (typeof exp !== "number") return true;

	const now = Math.floor(Date.now() / 1000);
	return exp > now + TOKEN_EXPIRY_BUFFER_SECONDS;
}

async function getAppBridgeIdToken(): Promise<string | null> {
	if (!window.shopify) return null;

	let lastError: unknown;
	for (const delayMs of TOKEN_RETRY_DELAYS_MS) {
		if (delayMs > 0) await wait(delayMs);

		try {
			const token = await window.shopify.idToken();
			if (token && isUsableJwt(token)) {
				return token;
			}
			lastError = new Error("App Bridge returned an expired idToken");
		} catch (err) {
			lastError = err;
		}
	}

	throw lastError instanceof Error
		? lastError
		: new Error("Failed to get App Bridge idToken");
}

async function readErrorResponse(
	response: Response,
): Promise<{ detail: string; body?: Record<string, unknown> }> {
	let detail = "";
	let body: Record<string, unknown> | undefined;
	try {
		body = await response.json();
		detail =
			(body && typeof body.error === "string" && body.error) ||
			(body && typeof body.message === "string" && body.message) ||
			"";
	} catch {
		// body wasn't JSON — fall through to generic message
	}

	return { detail, body };
}

function isExpiredSignatureError(response: Response, detail: string): boolean {
	return (
		response.status === 401 &&
		detail.toLowerCase().includes("signature has expired")
	);
}

/**
 * Makes an authenticated fetch to the translation API.
 * Obtains a short-lived JWT from App Bridge and passes it as Bearer token.
 * Falls back gracefully when running outside Shopify Admin (no idToken available).
 */
export async function authenticatedFetch<T = unknown>(
	path: string,
	options: RequestInit = {},
): Promise<T> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
		...(options.headers as Record<string, string> | undefined),
	};

	// Attach App Bridge session token when available
	if (window.shopify) {
		try {
			const token = await getAppBridgeIdToken();
			if (token) {
				headers["Authorization"] = `Bearer ${token}`;
			}
		} catch (err) {
			console.warn("Failed to get App Bridge idToken:", err);
			throw new ApiError(
				"Failed to authenticate with Shopify. Refresh the app and try again.",
				401,
			);
		}
	}

	const shop = getShopDomain();
	const url = new URL(path, API_BASE_URL);
	if (shop && !url.searchParams.has("shop")) {
		url.searchParams.set("shop", shop);
	}

	const fetchWithHeaders = () =>
		fetch(url.toString(), {
			...options,
			headers,
		});

	let response = await fetchWithHeaders();

	if (!response.ok) {
		// Surface the backend's error message (e.g. from 402 Payment Required) to the UI.
		let { detail, body } = await readErrorResponse(response);

		if (isExpiredSignatureError(response, detail) && window.shopify) {
			try {
				const token = await getAppBridgeIdToken();
				if (token) {
					headers["Authorization"] = `Bearer ${token}`;
					response = await fetchWithHeaders();
					if (response.ok) return response.json() as Promise<T>;
					({ detail, body } = await readErrorResponse(response));
				}
			} catch (err) {
				console.warn("Failed to refresh App Bridge idToken:", err);
			}
		}

		// Backend signals the shop's SSM token is gone (never installed, or
		// refresh_token rejected by Shopify). Hop the merchant to OAuth — the
		// embedded UI has nothing useful to show until they reinstall.
		if (
			response.status === 401 &&
			body &&
			body.reason === "not_installed" &&
			typeof body.install_url === "string"
		) {
			window.top!.location.href = body.install_url;
		}

		throw new ApiError(
			detail || `API error ${response.status}: ${response.statusText}`,
			response.status,
			body,
		);
	}

	return response.json() as Promise<T>;
}
